import {
    safePromise,
    execAndWaitForAll
} from "../../../Helpers/Functions/promise"
import {
    APIAppInstanceModel,
    APIAppInstancePatch,
    AppInstanceListParams,
    AppInstanceStatus,
    defaultParams,
    JobWithFeedbackStats
} from "../../../Models/API/AppInstanceModel"
import { TemplateName } from "../../../Models/API/AppTemplateModel"
import { hasMore } from "../../../Models/Client/RemoteData"
import { MainThunkDispatch, State, MainThunkResult } from "../../../Reducers"
import { apiEndpoints } from "../../../Routes/api"
import { failMessage, successMessage } from "../../Client/MessagesActions"
import { apiFetch, killPoll, pollAPI } from "../../Client/NetworkActions"
import { InstanceAPIType } from "../../Types"
import { BatchListModel } from "../../../Models/API/BatchListModel"
import { APITagListModel } from "../../../Models/API/APITagListModel"
import { APIPlans } from "../../../Models/API/APIAppPlans"
import { LabelerConfig } from "../../../Models/DataProgram/LabelerRestriction"
import { APIAppMeasurerConfiguration } from "../../../Models/API/APIAppMeasurerConfiguration"
import { getValue, hasValue } from "../../../Models/Client/FetchState"
import { selectUserName } from "../../../Helpers/Functions/Selectors/auth"
import { APIAppAsyncOperationModel } from "../../../Models/API/APIAppAsyncOperationModel"
import { MetricFields } from "../../../Models/API/AppMetricModel"
import {
    AppDownloadSettings,
    AppSettings,
    AppTTLSettings
} from "../../../Models/API/AppSettings"

export const createInstance =
    (
        orgUsername: string | undefined,
        templateName: TemplateName,
        params: any,
        canoticCrowd: boolean = true,
        squashSuccessMessage: boolean = false,
        onSuccessMessage: string = "Successfully created project",
        onErrorMessage: string = "Could not create project",
        projectName: string = "default"
    ) =>
    async (
        dispatch: MainThunkDispatch
    ): Promise<APIAppInstanceModel | undefined> => {
        const options: RequestInit = {
            method: "POST",
            body: JSON.stringify({
                templateName,
                projectName,
                appParams: params || null,
                appMetrics: null,
                metadata: null,
                canoticCrowd
            })
        }

        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `CREATE_INSTANCE_${templateName}`,
                    apiEndpoints.createInstance({ org: orgUsername }),
                    options
                )
            )
        )

        if (error !== undefined || data === undefined) {
            if (error !== undefined) {
                dispatch(failMessage(onErrorMessage, error))
            }

            return
        }

        if (!squashSuccessMessage) {
            dispatch(successMessage(onSuccessMessage))
        }

        dispatch({
            type: InstanceAPIType.addInstance,
            payload: data
        })

        return Promise.resolve(data as APIAppInstanceModel)
    }

export const cloneInstance =
    (
        appUUID: string,
        params: any,
        canoticCrowd: boolean = true,
        squashSuccessMessage: boolean = false,
        onSuccessMessage: string = "Successfully cloned project",
        onErrorMessage: string = "Could not clone project",
        projectName: string = "default",
        constraints?: LabelerConfig
    ) =>
    async (
        dispatch: MainThunkDispatch
    ): Promise<APIAppInstanceModel | undefined> => {
        const options: RequestInit = {
            method: "POST",
            body: JSON.stringify({
                appName: projectName,
                appParams: params || null,
                canoticCrowd,
                constraints
            })
        }

        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `CLONE_INSTANCE_${projectName}`,
                    apiEndpoints.cloneInstance({ appUUID }),
                    options
                )
            )
        )

        if (error !== undefined || data === undefined) {
            if (error !== undefined) {
                dispatch(failMessage(onErrorMessage, error))
            }

            return
        }

        if (!squashSuccessMessage) {
            dispatch(successMessage(onSuccessMessage))
        }

        dispatch({
            type: InstanceAPIType.addInstance,
            payload: data
        })

        return Promise.resolve(data as APIAppInstanceModel)
    }

export const loadMoreInstanceList =
    (orgUsername: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const data = getState().api.apps.apps

        if (data.list !== undefined && hasMore(data)) {
            await dispatch(
                getInstanceList(
                    orgUsername,
                    data.list.params,
                    data.list.pages.length
                )
            )
        }
    }

export const getInstanceList =
    (
        orgUsername: string,
        params: AppInstanceListParams,
        page: number = 0,
        pageSize: number = 20
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const allParams = { ...params, page, pageSize }
        const endpoint =
            selectUserName(getState()) === orgUsername
                ? apiEndpoints.getInstanceList(allParams)
                : apiEndpoints.getOrgInstanceList({
                      ...allParams,
                      orgName: orgUsername
                  })
        const { data, error } = await safePromise<any>(
            dispatch(apiFetch(endpoint.url, endpoint))
        )

        if (error !== undefined || data === undefined || !data.apps) {
            return
        }

        dispatch({
            type: InstanceAPIType.setList,
            meta: {
                total: data.total,
                page,
                params,
                totalArchived: data.totalArchivedApps
            },
            payload: data.apps
        })
    }

export const clearInstanceList = () => async (dispatch: MainThunkDispatch) => {
    dispatch({ type: InstanceAPIType.clearList })
}

export const getInstance =
    (id: string) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `GET_INSTANCE_${id}`,
                    apiEndpoints.getInstanceByID({ id })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            if (error !== undefined) {
                dispatch({
                    type: InstanceAPIType.addError,
                    meta: {
                        appID: id
                    },
                    payload: error.message
                })
            }

            return
        }

        dispatch({
            type: InstanceAPIType.addInstance,
            payload: data
        })
    }

export const startMonitorApp =
    (instanceID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const endpoint = apiEndpoints.getInstanceByID({
            id: instanceID
        })

        const handleResponse = async (
            data: APIAppInstanceModel | undefined
        ) => {
            if (
                data === undefined ||
                // skip if there's ongoing batch, in order to prevent different
                // UI elements from updating separately (asynchronously)
                (getValue(getState().api.apps.apps.records.get(instanceID))
                    ?.batchProcessCount ?? 0) > 0
            ) {
                return
            }

            dispatch({
                type: InstanceAPIType.addInstance,
                payload: data
            })
        }

        dispatch(
            pollAPI<APIAppInstanceModel>(
                "INSTANCE_POLL",
                endpoint,
                handleResponse,
                undefined,
                3000
            )
        )
    }

export const stopMonitorApp = () => async (dispatch: MainThunkDispatch) => {
    dispatch(killPoll("INSTANCE_POLL"))
}

export const updateInstance =
    (
        id: string,
        patchProperties: APIAppInstancePatch,
        onSuccessMessage?: string,
        onErrorMessage?: string
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getInstanceByID({ id })
        const options = {
            method: "PATCH",
            body: JSON.stringify(patchProperties)
        }
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<APIAppInstanceModel>(
                    `UPDATE_INSTANCE_${id}`,
                    endpoint,
                    options
                )
            )
        )

        if (error !== undefined || data === undefined) {
            if (error !== undefined && onErrorMessage !== undefined) {
                dispatch(failMessage(onErrorMessage, error))
            }
            throw error ?? Error(`Update instance ${id} did not return data.`)
        }

        if (onSuccessMessage !== undefined) {
            dispatch(successMessage(onSuccessMessage))
        }

        dispatch({
            type: InstanceAPIType.addInstance,
            payload: data
        })

        return data
    }

export const updateInstanceName =
    (id: string, name: string) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        await dispatch(
            updateInstance(
                id,
                { name },
                "Project name is updated",
                "Could not update project name"
            )
        )
    }

export const updateInstanceCrowdStatus =
    (id: string, status: boolean) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        await dispatch(
            updateInstance(
                id,
                { canoticCrowd: status },
                `super.AI crowd is now ${status ? "active" : "deactivated"}`,
                "Could not change who should process"
            )
        )
    }

export const archiveApps =
    (
        orgUsername: string,
        appIDs: string[],
        squashMessages = false
    ): MainThunkResult<Promise<void>> =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<void> => {
        try {
            await execAndWaitForAll(
                appIDs.map(appID =>
                    dispatch(
                        updateInstance(
                            appID,
                            { status: AppInstanceStatus.DISABLED },
                            undefined,
                            undefined
                        )
                    )
                )
            )
            if (!squashMessages) {
                dispatch(
                    successMessage("Successfully archived selected projects")
                )
            }
        } catch (e) {
            if (!squashMessages) {
                dispatch(failMessage("Could not archive all projects", e))
            }
            throw e
        } finally {
            const params =
                getState().api.apps.apps.list?.params ?? defaultParams

            // refreshed the model mostly to get updated archived count
            dispatch(getInstanceList(orgUsername, params)).catch(() => null)
        }
    }

export const unArchiveApps =
    (orgUsername: string, appIDs: string[]): MainThunkResult<Promise<any>> =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<void> => {
        try {
            await execAndWaitForAll(
                appIDs.map(appID =>
                    dispatch(
                        updateInstance(
                            appID,
                            { status: AppInstanceStatus.ENABLED },
                            undefined,
                            undefined
                        )
                    )
                )
            )

            dispatch(
                successMessage("Successfully unarchived selected projects")
            )
        } catch (e) {
            dispatch(failMessage("Could not unarchive all projects", e))
            throw e
        } finally {
            const params =
                getState().api.apps.apps.list?.params ?? defaultParams

            // refreshed the model mostly to get updated archived count
            dispatch(getInstanceList(orgUsername, params)).catch(() => null)
        }
    }

export const updateAppSettingsSysAdmin =
    (
        appID: string,
        selectedWorkflow: string | undefined,
        dpWorkflows: string[],
        constraints: LabelerConfig,
        canoticCrowd: boolean,
        cancelAndRelabelJobsInState: string[],
        measurerConfiguration?: APIAppMeasurerConfiguration,
        measurerMetrics?: MetricFields[],
        settings?: AppSettings
    ) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        try {
            await dispatch(
                updateInstance(
                    appID,
                    {
                        dpWorkflows,
                        selectedWorkflow,
                        constraints,
                        canoticCrowd,
                        cancelAndRelabelJobsInState,
                        settings
                    },
                    undefined,
                    undefined
                )
            )

            if (measurerConfiguration !== undefined) {
                await dispatch(
                    updateMeasurerConfiguration(appID, measurerConfiguration)
                )
            }
            if (measurerMetrics !== undefined) {
                await dispatch(
                    updateAppMeasurerMetricRegistration(
                        appID,
                        measurerMetrics.reduce(
                            (agg, metricField) => ({ ...agg, ...metricField }),
                            {} as MetricFields
                        )
                    )
                )
            }

            dispatch(successMessage("Changes saved"))
        } catch (e) {
            dispatch(failMessage("Could not update project", e))
        }
    }

export const updateAppTTLSettings =
    (
        appID: string,
        cancelAndRelabelJobsInState: string[],
        ttlSettings: AppTTLSettings
    ) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        try {
            await dispatch(
                updateInstance(appID, {
                    cancelAndRelabelJobsInState,
                    settings: {
                        ttlSettings
                    }
                })
            )
            dispatch(successMessage("TTL settings saved"))
        } catch (e) {
            dispatch(failMessage("Could not update project", e))
        }
    }

export const updateAppSettingsCustomer =
    (
        appID: string,
        selectedWorkflow: string | undefined,
        canoticCrowd: boolean,
        cancelAndRelabelJobsInState: string[],
        settings: AppSettings
    ) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        await dispatch(
            updateInstance(
                appID,
                {
                    selectedWorkflow,
                    canoticCrowd,
                    cancelAndRelabelJobsInState,
                    settings
                },
                "Changes saved",
                "Could not change project plan"
            )
        )
    }

export const startMonitorProcessingBatchList =
    (appID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const endpoint = apiEndpoints.listProcessingBatches({
            appUUID: appID
        })

        const handleResponse = async (data: BatchListModel | undefined) => {
            if (
                data === undefined ||
                // skip if there's ongoing batch, in order to prevent different
                // UI elements from updating separately (asynchronously)
                getState().api.apps.apps.records.some(
                    r =>
                        hasValue(r) &&
                        r.value.batchProcessCount > 0 &&
                        (r.value.isSubmittingNewJob || false)
                )
            ) {
                return
            }

            dispatch({
                type: InstanceAPIType.setProcessingBatchList,
                meta: { appID },
                payload: data
            })
        }

        dispatch(
            pollAPI<BatchListModel>(
                "LIST_PROCESSING_BATCHES",
                endpoint,
                handleResponse,
                undefined,
                3000
            )
        )
    }

export const stopMonitorProcessingBatchList =
    () => async (dispatch: MainThunkDispatch) => {
        dispatch(killPoll("LIST_PROCESSING_BATCHES"))
    }

export const listTags =
    (appID: string, key: string, keyword: string) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.listTags({ appID, keyword })
        const { data, error } = await safePromise<APITagListModel>(
            dispatch(apiFetch(endpoint.url, endpoint))
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: InstanceAPIType.setTagList,
            meta: { appID, key, keyword },
            payload: data.jobs.map(e => e.tag)
        })
    }

export const reloadTags =
    (appID: string) => (dispatch: MainThunkDispatch, getState: () => State) => {
        const entries = getState().api.apps.tags[appID]

        if (entries === undefined) {
            return
        }

        for (const [key, remoteData] of entries) {
            if (remoteData.list !== undefined) {
                dispatch(listTags(appID, key, remoteData.list.params.keyword))
            }
        }
    }

export const getPlansMetadata =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        try {
            const data = await dispatch(
                apiFetch<APIPlans>(
                    `GET_PLANS_METADATA_LIST_${appID}`,
                    apiEndpoints.listPlansMetadata({ id: appID })
                )
            )

            if (data !== undefined) {
                dispatch({
                    type: InstanceAPIType.setPlansMetadata,
                    meta: {
                        appID
                    },
                    payload: data.dpWorkflowsMetadata
                })
            }
        } catch (ignore) {}
    }

export const getMeasurerConfiguration =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        try {
            const data = await dispatch(
                apiFetch<APIAppMeasurerConfiguration>(
                    `GET_MEASURER_CONFIGURATION_${appID}`,
                    apiEndpoints.measurerConfiguration({ id: appID })
                )
            )

            if (data !== undefined) {
                dispatch({
                    type: InstanceAPIType.setMeasurerConfiguration,
                    meta: {
                        appID
                    },
                    payload: data
                })
            }
        } catch (ignore) {}
    }

const updateMeasurerConfiguration =
    (appID: string, config: APIAppMeasurerConfiguration) =>
    async (dispatch: MainThunkDispatch) => {
        await dispatch(
            apiFetch<APIAppMeasurerConfiguration>(
                `PATCH_MEASURER_CONFIGURATION_${appID}`,
                apiEndpoints.measurerConfiguration({ id: appID }),
                { method: "PATCH", body: JSON.stringify(config) },
                undefined,
                true
            )
        )

        await dispatch(getMeasurerConfiguration(appID))
    }

export const getJobWithFeedbackStats =
    (appUUID: string) => async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getJobWithFeedbackStats({
            appUUID
        })

        const { data, error } = await safePromise(
            dispatch(
                apiFetch<JobWithFeedbackStats>(
                    `GET_JOB_WITH_FEEDBACK_STATS_${appUUID}`,
                    endpoint
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: InstanceAPIType.addJobWithFeedbackStats,
            meta: { appID: appUUID },
            payload: data
        })
    }

export const updateOwnerActivity =
    (appUUID: string) => async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.updateOwnerActivity({
            appUUID
        })

        // update state to set 0 unread and then update backend
        dispatch({
            type: InstanceAPIType.resetUnreadMessagesCount,
            meta: { appID: appUUID }
        })
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<unknown>(
                    `UPDATE_OWNER_ACTIVITY_${appUUID}`,
                    endpoint,
                    {
                        method: "PUT"
                    }
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        //update stats
        dispatch(getJobWithFeedbackStats(appUUID))
    }

export const getAppMetrics =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `GET_APP_METRICS_${appID}`,
                    apiEndpoints.getMetricsByAppID({ appID })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: InstanceAPIType.setMetricsByAppID,
            meta: { appID },
            payload: data
        })
    }

type Response = { content: APIAppAsyncOperationModel[] }

export const startMonitorAsyncOperations =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getAsyncOperations({
            appID,
            statusIn: ["IN_PROGRESS", "SCHEDULED", "FAILED"],
            typeIn: [
                "APP_UPDATED",
                "CANCEL",
                "DELETE",
                "RELABEL",
                "START",
                "POST_PROCESS"
            ]
        })
        const handleResponse = async (data: Response | undefined) => {
            if (data === undefined) {
                return
            }

            dispatch({
                type: InstanceAPIType.setAsyncOperations,
                meta: { appID },
                payload: data.content
            })
        }

        dispatch(
            pollAPI<Response>(
                `ASYNC_OPERATIONS_POLL_${appID}`,
                endpoint,
                handleResponse,
                undefined,
                3000
            )
        )
    }

export const getAsyncOperations =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getAsyncOperations({
            appID,
            statusIn: ["IN_PROGRESS", "SCHEDULED", "FAILED"],
            typeIn: [
                "APP_UPDATED",
                "CANCEL",
                "DELETE",
                "RELABEL",
                "START",
                "POST_PROCESS"
            ]
        })

        const { data, error } = await safePromise<Response>(
            dispatch(apiFetch(`ASYNC_OPERATIONS_FETCH_${appID}`, endpoint))
        )

        if (data === undefined || error !== undefined) {
            return
        }

        dispatch({
            type: InstanceAPIType.setAsyncOperations,
            meta: { appID },
            payload: data.content
        })
    }

export const stopMonitorAsyncOperations =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        dispatch(killPoll(`ASYNC_OPERATIONS_POLL_${appID}`))
    }

export const retryAsyncOperation =
    (appID: string, operationID: number) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.retryAsyncOperation({
            appID,
            id: operationID
        })

        const { data, error } = await safePromise<APIAppAsyncOperationModel>(
            dispatch(
                apiFetch(`ASYNC_OPERATIONS_RETRY_${appID}`, endpoint, {
                    method: "POST"
                })
            )
        )

        if (data === undefined || error !== undefined) {
            return
        }

        await dispatch(getAsyncOperations(appID))
    }

export const archiveAsyncOperation =
    (appID: string, operationID: number) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getAsyncOperation({
            appID,
            id: operationID
        })

        const { data, error } = await safePromise<APIAppAsyncOperationModel>(
            dispatch(
                apiFetch(`ASYNC_OPERATIONS_RETRY_${appID}`, endpoint, {
                    method: "DELETE"
                })
            )
        )

        if (data === undefined || error !== undefined) {
            return
        }

        await dispatch(getAsyncOperations(appID))
    }

export const updateAppMeasurerMetricRegistration =
    (appID: string, metric_names_fields: MetricFields) =>
    async (dispatch: MainThunkDispatch) => {
        await dispatch(
            apiFetch(
                `PUT_APP_MEASURER_METRIC_${appID}`,
                apiEndpoints.getMetricsByAppID({ appID }),
                {
                    method: "PUT",
                    body: JSON.stringify({ metric_names_fields })
                },
                undefined,
                true
            )
        )

        await dispatch(getAppMetrics(appID))
    }
/**
 * Helps in getting all jobs count inculding deleted and relabeled
 *  */
export const getTotalJobCount =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<number>(
                    `GET_TOTAL_JOB_COUNT_${appID}`,
                    apiEndpoints.getTotalJobCount({
                        appID
                    })
                )
            )
        )

        if (data === undefined || error !== undefined) {
            return
        }

        dispatch({
            type: InstanceAPIType.setTotalJobCount,
            meta: { appID },
            payload: data
        })
    }

export const updateAppDownloadSettings =
    (appID: string, downloadSettings: AppDownloadSettings) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        await dispatch(
            updateInstance(
                appID,
                { settings: { downloadSettings } },
                "Changes saved",
                "Could not update download settings"
            )
        )
    }

export const turnOffAppDownloadSave =
    (appID: string) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        await dispatch(
            updateInstance(
                appID,
                { settings: { downloadSettings: { saveSettings: false } } },
                "Changes saved",
                "Could not turn off download save"
            )
        )
    }
